import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { OnSaleService } from '../../services/on-sale.service';
import { AuthService } from '../../services/auth.service';
import { AppStateService } from '../../services/app-state.service';
import { UploadDialogService } from '../../components/upload-dialog/upload-dialog.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-featured-frame-detail',
  templateUrl: './featured-frame-detail.component.html'
})
export class FeaturedFrameDetailComponent implements OnInit, OnDestroy {
  isLoading = true;
  onSale: any = {};

  // Frame & Mount
  frame: any = {};
  mount1: any = {};
  mount2: any = {};
  mount3: any = {};

  // Price
  finalPrice = 0;
  salePrice = 0;
  discountPercent = 0;
  flatDiscount = 15;

  // Size
  finalSize = '';
  printSize = '';
  frameWidth = 0;
  frameHeight = 0;

  // Images
  layoutSpaceFrames: any[] = [];
  activeImageIndex = 0;
  selectedImageUrl = '';

  // View mode
  productSubType = 'space-1';

  // Frame selector
  selectedFrameId = 0;
  framesData: any[] = [];
  isFramesLoading = false;

  // Frame border CSS
  frameBorderWidth = 0;
  frameBorderImage = '';

  // Mount CSS
  mat1Padding = 0;
  mount1Background = '';
  mat2Padding = 0;
  mount2Background = '';
  mat3Padding = 0;
  mount3Background = '';

  // Preview sizing
  printSizeH = '250px';
  printSizeHRoom = '125px';

  // Upload (uses UploadDialogService)
  image_path = '';
  is_image_uploaded = 0;
  my_photos: string[] = [];
  private uploadSub: Subscription | undefined;

  // Room preview images (hardcoded)
  roomImages = [
    { type: 'bed_room', thumb: 'assets/images/mahattaart/product_customization/bed_room_room_thumb.jpg', full: 'assets/images/mahattaart/product_customization/bed_room_room.png' },
    { type: 'chair_room', thumb: 'assets/images/mahattaart/product_customization/chair_room_thumb.jpg', full: 'assets/images/mahattaart/product_customization/chair_room.png' },
    { type: 'sofa_room', thumb: 'assets/images/mahattaart/product_customization/sofa_room_thumb.jpg', full: 'assets/images/mahattaart/product_customization/sofa_room.png' },
    { type: 'sofa_white_room', thumb: 'assets/images/mahattaart/product_customization/sofa_white_room_thumb.jpg', full: 'assets/images/mahattaart/product_customization/sofa_white_room.png' },
  ];

  // Featured add-to-cart now writes to the NEW backend's site cart.
  private siteApi = `${environment.newApiUrl}/api/site`;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onSaleService: OnSaleService,
    private authService: AuthService,
    private appState: AppStateService,
    private uploadDialogService: UploadDialogService,
    private http: HttpClient,
    private dataLayer: DataLayerService,
  ) {}

  ngOnInit() {

    console.log("componetn loaded with caption:", this.route.snapshot.paramMap.get('caption'));
    // Load saved photos from localStorage
    try { this.my_photos = JSON.parse(localStorage.getItem('my_photos') || '[]'); } catch { this.my_photos = []; }

    // Listen for upload dialog results
    this.uploadSub = this.uploadDialogService.result$.subscribe(result => {
      if (result) {
        this.image_path = result.url;
        this.is_image_uploaded = 1;
        try { this.my_photos = JSON.parse(localStorage.getItem('my_photos') || '[]'); } catch { this.my_photos = []; }
        this.productSubType = 'frame-preview';
        this.activeImageIndex = -1;
      }
    });

    this.route.params.subscribe(params => {
      const val = params.caption || params.id;
      if (val && !isNaN(+val)) {
        this.loadById(+val);
      } else if (val) {
        this.loadByCaption(val);
      }
    });
  }

  ngOnDestroy() {
    if (this.uploadSub) this.uploadSub.unsubscribe();
  }

  private loadByCaption(caption: string) {
    this.isLoading = true;
    const userId = this.authService.currentUser?.id || 0;
    this.onSaleService.findByCaption(caption, userId).subscribe({
      next: (res: any) => {
        this.onSale = res?.data || res;
        this.layoutSpaceFrames = this.onSale?.image_arr || [];
        this.setInitialData();
        this.loadAvailableFrames();
        this.isLoading = false;
        this.pushFramePromoView();
      },
      error: () => { this.isLoading = false; }
    });
  }

  private loadById(id: number) {
    this.isLoading = true;
    const userId = this.authService.currentUser?.id || 0;
    this.onSaleService.find(id, userId).subscribe({
      next: (res: any) => {
        this.onSale = res?.data || res;
        this.layoutSpaceFrames = this.onSale?.image_arr || [];
        this.setInitialData();
        this.loadAvailableFrames();
        this.isLoading = false;
        this.pushFramePromoView();
      },
      error: () => { this.isLoading = false; }
    });
  }

  private pushFramePromoView(): void {
    // view_promotion now fires on click via pushSelectPromotion
    return;
  }

  private setInitialData() {
    this.frame = this.onSale?.frame || {};
    this.mount1 = this.onSale?.mount1 || {};
    this.mount2 = this.onSale?.mount2 || {};
    this.mount3 = this.onSale?.mount3 || {};
    this.selectedFrameId = this.frame?.id || 0;

    // Initial prices from onSale object
    this.finalPrice = this.onSale?.list_price_tax || 0;
    this.salePrice = this.onSale?.net_price_tax || 0;
    this.discountPercent = this.onSale?.discount || 0;

    this.printSize = this.onSale?.image_size || '';
    this.calculateFinalSize();
    this.updateFrameBorderImage();
    this.updateMounts();
    this.calculatePreviewSize();

    // Use original onSale prices (list_price_tax / net_price_tax) — no recalculation needed

    // Set initial image
    if (this.layoutSpaceFrames.length > 0) {
      this.activeImageIndex = 0;
      this.selectedImageUrl = this.layoutSpaceFrames[0]?.image_path || '';
      this.productSubType = this.layoutSpaceFrames[0]?.type || 'space-1';
    }
  }

  // Get price from API for current frame (called on init + frame change)
  private calculatePrice() {
    if (!this.onSale?.id || !this.frame?.id) return;
    this.onSaleService.getFeaturedFramePrice(this.onSale.id, this.frame.id).subscribe({
      next: (res: any) => {
        if (res?.data) {
          this.finalPrice = res.data.total || this.finalPrice;
          this.salePrice = res.data.sub_total || this.salePrice;
          this.discountPercent = res.data.discount ?? this.discountPercent;
        }
      },
      error: () => {
        // Keep original onSale prices if API fails
      }
    });
  }

  private loadAvailableFrames() {
    if (!this.onSale?.id) return;
    this.isFramesLoading = true;
    this.onSaleService.getFeaturedFrame(this.onSale.id).subscribe({
      next: (res: any) => {
        this.framesData = res?.data || res?.frames || [];
        if (!Array.isArray(this.framesData)) this.framesData = [];
        this.isFramesLoading = false;
      },
      error: () => { this.isFramesLoading = false; }
    });
  }

  // ═══ SIZE CALCULATION ═══
  private calculateFinalSize() {
    if (!this.onSale?.image_size) return;
    const arr = this.onSale.image_size.split('X');
    if (arr.length < 2) return;
    let w = +arr[0].trim();
    let h = +arr[1].trim();

    const isFraming = this.onSale?.product_type?.slug === 'framing' ||
                      this.onSale?.product_type?.slug === 'frame_only';

    if (this.frame?.width_in && isFraming) {
      w += this.frame.width_in * 2;
      h += this.frame.width_in * 2;
    }
    if (this.onSale?.mount1_size) {
      w += this.onSale.mount1_size * 2;
      h += this.onSale.mount1_size * 2;
    }
    if (this.onSale?.mount2_size) {
      w += this.onSale.mount2_size * 2;
      h += this.onSale.mount2_size * 2;
    }
    if (this.onSale?.mount3_size) {
      w += this.onSale.mount3_size * 2;
      h += this.onSale.mount3_size * 2;
    }

    this.frameWidth = w;
    this.frameHeight = h;
    this.finalSize = `${w} X ${h}`;
  }

  // ═══ FRAME BORDER CSS ═══
  private updateFrameBorderImage() {
    if (!this.frame?.whole_frame_image) {
      this.frameBorderWidth = 0;
      this.frameBorderImage = '';
      return;
    }

    const widthIn = this.frame.width_in || 1;
    let borderWidth = widthIn * 10;

    if (widthIn <= 1.5) borderWidth += 10;
    else borderWidth += 5;

    let slice = 60;
    if (widthIn <= 1) slice = 60;
    else if (widthIn <= 1.5) slice = 70;
    else if (widthIn <= 2) slice = 130;
    else if (widthIn <= 2.5) slice = 130;
    else if (widthIn <= 3) slice = 145;
    else if (widthIn <= 4) slice = 160;
    else if (widthIn <= 5) slice = 210;

    this.frameBorderWidth = borderWidth;
    this.frameBorderImage = `url('${this.frame.whole_frame_image}') ${slice} stretch stretch`;
  }

  // ═══ MOUNT CSS ═══
  private updateMounts() {
    const matCalc = 8;

    if (this.mount1?.image_1 && this.onSale?.mount1_size) {
      this.mat1Padding = this.onSale.mount1_size * matCalc;
      this.mount1Background = `rgba(0,0,0,0) url('${this.mount1.image_1}') repeat scroll 0% 0%`;
    } else {
      this.mat1Padding = 0;
      this.mount1Background = '';
    }

    if (this.mount2?.image_1 && this.onSale?.mount2_size) {
      this.mat2Padding = this.onSale.mount2_size * matCalc;
      this.mount2Background = `rgba(0,0,0,0) url('${this.mount2.image_1}') repeat scroll 0% 0%`;
    } else {
      this.mat2Padding = 0;
      this.mount2Background = '';
    }

    if (this.mount3?.image_1 && this.onSale?.mount3_size) {
      this.mat3Padding = this.onSale.mount3_size * matCalc;
      this.mount3Background = `rgba(0,0,0,0) url('${this.mount3.image_1}') repeat scroll 0% 0%`;
    } else {
      this.mat3Padding = 0;
      this.mount3Background = '';
    }
  }

  // ═══ PREVIEW SIZE ═══
  private calculatePreviewSize() {
    if (!this.onSale?.image_size) return;
    const arr = this.onSale.image_size.split('X');
    if (arr.length < 2) return;
    const h = +arr[1].trim();

    let calcH = 2 * (h - 1) + 150;
    const totalMount = this.mat1Padding + this.mat2Padding + this.mat3Padding;
    if ((calcH + totalMount * 2) > 350) {
      calcH = 1.5 * (h - 1) + 150;
    }

    this.printSizeH = calcH + 'px';
    this.printSizeHRoom = (calcH / 2) + 'px';
  }

  // ═══ SIDEBAR CLICK ═══
  selectImage(index: number) {
    this.activeImageIndex = index;
    this.selectedImageUrl = this.layoutSpaceFrames[index]?.image_path || '';
    this.productSubType = this.layoutSpaceFrames[index]?.type || 'space-' + (index + 1);
  }

  onProductSubType(type: string) {
    this.productSubType = type;
    this.activeImageIndex = -1;
  }

  showFramePreview() {
    this.productSubType = 'frame-preview';
    this.activeImageIndex = -1;
  }

  showRoomPreview(room: any) {
    this.productSubType = room.type;
    this.activeImageIndex = -1;
  }

  // ═══ FRAME SELECTOR ═══
  selectFrame(frameItem: any) {
    this.selectedFrameId = frameItem.id;
    this.frame = frameItem;
    this.calculateFinalSize();
    this.updateFrameBorderImage();
    this.calculatePreviewSize();
  }

  // ═══ UPLOAD (via UploadDialogService) ═══
  onAddPhoto(event?: Event) {
    if (event) event.stopPropagation();
    this.uploadDialogService.open(0, 'Featured Frame Photo');
    this.productSubType = 'frame-preview';
  }

  // Click existing photo from gallery
  onPhoto(event: Event, index: number) {
    event.stopPropagation();
    this.image_path = this.my_photos[index];
    this.is_image_uploaded = 1;
    this.productSubType = 'frame-preview';
    this.activeImageIndex = -1;
  }

  // Remove photo from gallery
  onRemovePhoto(event: Event, index: number) {
    event.stopPropagation();
    if (this.my_photos[index] === this.image_path) {
      this.appState.showToast('Cannot delete the currently active image', 'info');
      return;
    }
    this.my_photos.splice(index, 1);
    localStorage.setItem('my_photos', JSON.stringify(this.my_photos));
  }

  // ═══ ADD TO CART ═══
  onAddToCart() {
    const productSlug = this.onSale?.product?.slug || '';
    const ptSlug = this.onSale?.product_type?.slug || '';

    // Validation: image required for frame_your_photos (except frame_only, wall_art, wall_paper)
    if (productSlug !== 'wall_art'
        && productSlug !== 'wall_paper'
        && this.is_image_uploaded === 0
        && ptSlug !== 'frame_only') {
      this.appState.showToast('Please upload Image', 'info');
      return;
    }

    const user = this.authService.currentUser;
    if (user && (Number(user.customers_id) || Number(user.id)) > 0) {
      this.addToCartLoggedIn(this.onSale.id, Number(user.customers_id) || Number(user.id));
    } else {
      this.addToCartGuest(this.onSale.id);
    }
  }

  private get authHeaders() {
    const token = localStorage.getItem('auth_token');
    const h: any = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return { headers: h };
  }

  private get sessionId(): string {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }

  private addToCartLoggedIn(onSaleId: number, customerId: number) {
    // NEW backend: owner is resolved from the JWT (authHeaders); the config and
    // featured price are read from the SKU server-side, so we send only the SKU,
    // the chosen frame and the image.
    this.http.post(`${this.siteApi}/cart/on-sale`, {
      on_sale_id: onSaleId,
      image_path: this.image_path || '',
      width: 1,
      height: 1,
      installation_pincode: 0,
      frame_id: this.frame?.id || this.selectedFrameId
    }, this.authHeaders).subscribe({
      next: () => {
        this.appState.showToast('Product added to cart successfully!', 'cart');
        this.appState.loadCartFromApi(true);
        this.router.navigate(['/checkout']);
      },
      error: (err) => {
        console.error('addOnSaleItemToCart error:', err);
        this.appState.showToast('Failed to add to cart', 'info');
      }
    });
  }

  private addToCartGuest(onSaleId: number) {
    // NEW backend: no token, so owner() falls to the guest session id.
    this.http.post(`${this.siteApi}/cart/on-sale`, {
      on_sale_id: onSaleId,
      image_path: this.image_path || '',
      width: 1,
      height: 1,
      installation_pincode: 0,
      session_id: this.sessionId
    }).subscribe({
      next: () => {
        this.appState.showToast('Product added to cart successfully!', 'cart');
        this.appState.loadCartFromApi(true);
        this.router.navigate(['/checkout']);
      },
      error: (err) => {
        console.error('fillSaleItemToCart error:', err);
        this.appState.showToast('Failed to add to cart', 'info');
      }
    });
  }

  onBuyNow() { this.onAddToCart(); }

  // ═══ WISHLIST ═══
  onToggleWishlist() {
    const raw = this.salePrice
      || this.onSale?.net_price_tax
      || this.onSale?.net_price
      || this.onSale?.list_price_tax
      || this.onSale?.list_price
      || this.finalPrice
      || 0;
    this.appState.toggleWishlist(String(this.onSale.id), {
      id: String(this.onSale.id),
      name: this.onSale.title || this.onSale.caption,
      image: this.onSale.image_1 || this.selectedImageUrl,
      price: Number(raw) || 0,
      wallpaper_type: 'Featured',
      slug: this.onSale.title_slug
    });
  }

  isInWishlist(): boolean {
    return this.appState.wishlistIds.has(String(this.onSale?.id));
  }

  onShare() {
    if (navigator.share) {
      navigator.share({ title: this.onSale?.title || 'Featured Frame', url: window.location.href });
    }
  }

  // ═══ HELPERS ═══
  get frameName(): string {
    if (!this.frame?.frame_name) return '';
    return this.frame.frame_name + (this.frame.width_in ? ` (${this.frame.width_in}")` : '');
  }

  get showWallImages(): boolean {
    return this.onSale?.is_table_frame === 0 && this.onSale?.show_wall_images === 1;
  }

  get showUploadSection(): boolean {
    const slug = this.onSale?.product?.slug || '';
    return slug !== 'wall_art' && slug !== 'wall_paper';
  }

  get isOvalShape(): boolean {
    return !!this.onSale?.shape;
  }

  get showEditProduct(): boolean {
    return this.onSale?.is_edit_product === 1 && this.onSale?.url;
  }

  get defaultProductImage(): string {
    return this.onSale?.image_1 || this.onSale?.image_2 || '';
  }

  get currentPhotoInFrame(): string {
    return this.is_image_uploaded ? this.image_path : this.defaultProductImage;
  }

  getEditQueryParams(): any {
    return {
      product: this.onSale?.product?.slug,
      product_type: this.onSale?.product_type?.slug
    };
  }
}
